import { useMemo } from 'react';
import { addDays } from 'date-fns';
import moment from 'moment-timezone';
import type { DateSpanApi } from '@fullcalendar/core';
import type { SchedulingWindowState } from '../utils/types';

type WindowBounds = { start: Date; end: Date };

const computeRollingBounds = (
  rollingDays: string,
  timezone: string
): WindowBounds => {
  const days = Number(rollingDays);
  const start = moment.tz(timezone).startOf('day').toDate();
  return { start, end: addDays(start, days + 1) };
};

const computeCustomRangeBounds = (
  startDate: string | null,
  endDate: string | null,
  timezone: string
): WindowBounds | undefined => {
  if (!startDate || !endDate) {
    return undefined;
  }
  return {
    start: moment.tz(startDate, timezone).startOf('day').toDate(),
    end: addDays(moment.tz(endDate, timezone).startOf('day').toDate(), 1),
  };
};

const computeBounds = (
  schedulingWindow: SchedulingWindowState | undefined,
  timezone: string | undefined
): WindowBounds | undefined => {
  if (!schedulingWindow || !timezone) {
    return undefined;
  }
  if (schedulingWindow.windowType === 'rolling') {
    return computeRollingBounds(schedulingWindow.rollingDays, timezone);
  }
  return computeCustomRangeBounds(
    schedulingWindow.startDate,
    schedulingWindow.endDate,
    timezone
  );
};

const isWithinBounds = (
  span: { start: Date; end: Date },
  bounds: WindowBounds | undefined
): boolean => {
  return !bounds || (span.start >= bounds.start && span.end <= bounds.end);
};

export const useSchedulingWindowConstraints = (
  schedulingWindow: SchedulingWindowState | undefined,
  isValid: boolean,
  timezone: string | undefined
): {
  selectAllow: (span: DateSpanApi) => boolean;
  eventAllow: (dropInfo: { start: Date; end: Date }) => boolean;
  allowableRanges: Array<WindowBounds>;
} => {
  const bounds = useMemo(
    () =>
      isValid && schedulingWindow?.windowType !== 'rolling'
        ? computeBounds(schedulingWindow, timezone)
        : undefined,
    [schedulingWindow, isValid, timezone]
  );

  const allowableRanges = useMemo(() => {
    if (!bounds) {
      return [];
    }
    return [{ start: bounds.start, end: bounds.end }];
  }, [bounds]);

  return {
    selectAllow: (span) => isWithinBounds(span, bounds),
    eventAllow: (dropInfo) => isWithinBounds(dropInfo, bounds),
    allowableRanges,
  };
};
